import {
    ApplicationSettings,
    DictionaryService,
    EditManyDefinitionService,
    LdnHttp,
    ListDefinitionService,
    NotificationHubService,
    NotificationService,
    PreviewService,
    StateService,
    UserService
} from './services';
import { SkorowidzArtykulowyModule } from '../skorowidz-artykulowy/skorowidz-artykulowy.module';
import { MiejscaPublikacjiModule } from '../miejsca-publikacji/miejsca-publikacji.module';
import { AccessDeniedComponent } from '../shared';
import {
    AuthenticationService,
    AuthenticationServiceOptions,
    AuthorizationGuardOptions,
    AuthorizationModule
} from '../authorization';
import { ObiektListActionsService } from './store/obiekt-list/obiekt-list-actions.service';
import { ObiektListEffects } from './store/obiekt-list/obiekt-list.effects';
import { obiektListReducer } from './store/obiekt-list/obiekt-list.reducer';
import { ListDefinitionsEffects } from './store/list-definitions/list-definitions.effects';
import { listDefinitionsReducer } from './store/list-definitions/list-definitions.reducer';
import { ListDefinitionsActionsService } from './store/list-definitions/list-definitions.actions.service';
import { LdnFormBuilder, LdnTypedFormBuilder } from '../../lib/ldn-form-builder';
import { MetadataPreviewActionsService } from './store/obiekt-list/metadata-preview/metadata-preview-actions.service';
import { MetadataPreviewEffects } from './store/obiekt-list/metadata-preview/metadata-preview.effects';
import { layoutReducer } from './store/layout/layout.reducer';
import { LayoutActionsService } from './store/layout/layout.actions.service';
import { userReducer } from './store/user/user.reducer';
import { UserActionsService } from './store/user/user.actions.service';
import { UserEffects } from './store/user/user.effects';
import { EditManyDefinitionsActionsService } from './store/edit-many-definitions/edit-many-definitions-actions.service';
import { EditManyDefinitionsEffects } from './store/edit-many-definitions/edit-many-definitions-effects';
import { editManyDefinitionsReducer } from './store/edit-many-definitions/edit-many-definitions.reducer';
import { RetryInterceptor } from './interceptors/retry.interceptor';
import { requestsReducer } from './store/requests/requests.reducer';
import { NgModule, Provider, inject, provideAppInitializer } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { MessageService } from 'primeng/api';
import { Store, StoreModule } from '@ngrx/store';
import { TrescExportService } from '../tresc/tresc-export/tresc-export.service';
import { AuthorizationServiceOptions } from '../authorization/authorization-service-options.model';
import { selectUserContexts, selectUserSessionExpire } from './store/user/user.selectors';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { userForbiddenInfoHide, userSessionLoadStart } from './store/user/user.actions';

const securityProviders: Provider[] = [
    {
        provide: AuthenticationServiceOptions,
        useFactory: createAuthenticationServiceOptions,
        deps: [ApplicationSettings, Store]
    }, {
        provide: AuthorizationServiceOptions,
        useFactory: createAuthorizationServiceOptions,
        deps: [ApplicationSettings, Store]
    },
    {
        provide: AuthorizationGuardOptions,
        useValue: {unauthorizedRoutePath: AccessDeniedComponent.route}
    }
];

export function createAuthenticationServiceOptions(applicationSettings: ApplicationSettings, store: Store): AuthenticationServiceOptions {
    return {
        baseAddress: applicationSettings.webApiUrl,
        accessTokenRefreshTimeBeforeExpirationMs: applicationSettings.settings.authentication.accessTokenRefreshTimeBeforeExpirationMs,
        expire: store.select(selectUserSessionExpire)
    };
}

export function createAuthenticationInitializer(userActionsService: UserActionsService, store: Store, authenticationService: AuthenticationService) {
    //console.log('User initializer', document.readyState);
    return () => {
        userActionsService.loadCurrentUser();
        const sessionSynchronizationBroadcastChannel = new BroadcastChannel('sessionSynchronization');
        let currentExpirationDate: number;
        sessionSynchronizationBroadcastChannel.onmessage = (event) => {
            const newExpirationDate: number = +event.data;
            // Jeżeli wyszystkie konteksty same odświeżają sobie token, to sesję też będą.
            // Dlatego sprawdzamy czy już jest taka data wygaśnięcia jak z wiadomości,
            // aby uniknać wielokrotnego odpytywania o tą samą sesję.
            if (newExpirationDate === currentExpirationDate) {
                return;
            }
            if (currentExpirationDate) {
                userActionsService.reloadSessionInfo();
            } else {
                window.location.reload();
            }
        };

        store.select(selectUserSessionExpire)
            .pipe(filter(x => x !== null), distinctUntilChanged())
            .subscribe(x => {
                // Informacje o sesji zostały odświeżone. Jeżeli sesja jest aktualna (wygasa w przyszłości) to:
                // 1. Odświeżamy token przed upływem sesji
                // 2. Wysyłamy informację pozostałym kartą przez BroadcastChannel - wszystkie karty muszą mieć tą samą datę
                const expirationDate = new Date(x * 1000);
                if (new Date < expirationDate) {
                    currentExpirationDate = x;
                    authenticationService.autoRefreshAccessToken(expirationDate);
                    sessionSynchronizationBroadcastChannel.postMessage(x);
                    store.dispatch(userForbiddenInfoHide());
                }
            });

        authenticationService.accessTokenRefreshed.subscribe(() => {
            store.dispatch(userSessionLoadStart());
        });
    };
}

export function createAuthorizationServiceOptions(applicationSettings: ApplicationSettings, store: Store): AuthorizationServiceOptions {
    const result: AuthorizationServiceOptions = {
        baseAddress: applicationSettings.webApiUrl,
        contexts: () => []
    };
    store.select(selectUserContexts).subscribe(x => {
        result.contexts = () => x;
    });
    return result;
}

// @ts-ignore
@NgModule({ declarations: [], imports: [CommonModule,
        AuthorizationModule.forRoot(),
        MiejscaPublikacjiModule,
        SkorowidzArtykulowyModule,
        StoreModule.forRoot({
            obiektList: obiektListReducer,
            listDefinitions: listDefinitionsReducer,
            layout: layoutReducer,
            user: userReducer,
            editManyDefinitions: editManyDefinitionsReducer,
            requests: requestsReducer
        }, {
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true,
                // strictStateSerializability: true,
                // strictActionSerializability: true
            }
        }),
        StoreDevtoolsModule.instrument(),
        EffectsModule.forRoot([ObiektListEffects, ListDefinitionsEffects, MetadataPreviewEffects, UserEffects, EditManyDefinitionsEffects])], providers: [
        MessageService,
        LdnHttp,
        NotificationService,
        ListDefinitionService,
        StateService,
        DictionaryService,
        UserService,
        ApplicationSettings,
        securityProviders,
        ObiektListActionsService,
        MetadataPreviewActionsService,
        ListDefinitionsActionsService,
        LayoutActionsService,
        LdnFormBuilder,
        LdnTypedFormBuilder,
        PreviewService,
        TrescExportService,
        UserActionsService,
        NotificationHubService,
        EditManyDefinitionsActionsService,
        EditManyDefinitionService,
        provideAppInitializer(() => {
            const initializerFn = (createAuthenticationInitializer)(inject(UserActionsService), inject(Store), inject(AuthenticationService));
            return initializerFn();
        }),
        { provide: HTTP_INTERCEPTORS, useClass: RetryInterceptor, multi: true },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class CoreModule {
    constructor() {
    }
}
